import { AxisWarnings } from 'components/node_settings/V4EditSettingsModal/AdvancedSettingsTab/AxisWarnings';
import { EnvelopedDataSetting } from 'components/node_settings/V4EditSettingsModal/AdvancedSettingsTab/EnvelopedDataSetting';
import { ToggleSetting } from 'components/node_settings/V4Settings/ToggleSetting';
import { SectionDescription } from 'components/node_settings/V4EditSettingsModal/styled';
import { SettingsList } from 'components/node_settings/V4Settings/styled';
import {
    DEFAULT_TIMED,
    UPDATE_INTERVAL_MODE_LABELS,
    UpdateIntervalMode,
    V4SensorSettings,
} from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { useFeatureFlag } from 'configcat-react';
import { ReactNode, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

type AxisName = 'vibrationX' | 'vibrationY' | 'vibrationZ';

export function AdvancedSettings() {
    const { control, setValue, getValues } = useFormContext<V4SensorSettings>();
    const [vibrationX, vibrationY, vibrationZ, ultrasound, turningSpeed] = useWatch({
        control,
        name: ['vibrationX', 'vibrationY', 'vibrationZ', 'ultrasound', 'turningSpeed'],
    });

    const { value: zAxisEnabled } = useFeatureFlag('v4SensorsZAxis', false);
    const { value: envelopedDataEnabled } = useFeatureFlag(
        'v4SensorsEnvelopedData',
        false,
    );

    const [yEffects, setYEffects] = useState<{
        envelopedDisabled: boolean;
        lfcDisabled: boolean;
    } | null>(null);
    const [intervalChange, setIntervalChange] = useState<{
        from: Exclude<UpdateIntervalMode, 'timed'>;
        axis: AxisName;
    } | null>(null);

    const setAxis = (axis: AxisName, next: boolean) => {
        const prior = getValues();
        const nextAxes = {
            vibrationX: prior.vibrationX,
            vibrationY: prior.vibrationY,
            vibrationZ: prior.vibrationZ,
            [axis]: next,
        };
        const allAxesOff =
            !nextAxes.vibrationX && !nextAxes.vibrationY && !nextAxes.vibrationZ;

        setValue(axis, next, { shouldDirty: true, shouldValidate: true });

        // Update Interval shifts to Timed when all axes are disabled because SMARTsample and
        // Triggered need sampling data. The warning shows on whichever axis emptied the set,
        // so that axis is tracked alongside the prior mode. Coerced here because the Collection
        // tab unmounts while this tab is active and can't enforce the all-axes-off rule itself.
        if (allAxesOff && prior.updateInterval.mode !== 'timed') {
            setValue('updateInterval', DEFAULT_TIMED, {
                shouldDirty: true,
                shouldValidate: true,
            });
            setIntervalChange({ from: prior.updateInterval.mode, axis });
        } else {
            setIntervalChange(null);
        }

        if (axis !== 'vibrationY') {
            return;
        }

        if (next) {
            setYEffects(null);
            return;
        }

        // Enveloped Data and Low-Frequency Collection are dependent on the Y axis.
        setValue('envelopedData', { enabled: false }, { shouldDirty: true });
        setValue('lowFrequencyCollection', { enabled: false }, { shouldDirty: true });
        setYEffects({
            envelopedDisabled: prior.envelopedData.enabled,
            lfcDisabled: prior.lowFrequencyCollection.enabled,
        });
    };

    const axisWarnings = (axis: AxisName): ReactNode => {
        const messages: string[] = [];
        if (intervalChange?.axis === axis) {
            messages.push(
                `Update Interval changed from ${UPDATE_INTERVAL_MODE_LABELS[intervalChange.from]} to Timed`,
            );
        }
        if (axis === 'vibrationY') {
            if (yEffects?.envelopedDisabled) {
                messages.push('Enveloped Data is now disabled');
            }
            if (yEffects?.lfcDisabled) {
                messages.push('Low-Frequency collection is now disabled');
            }
        }
        return messages.length > 0 ? (
            <AxisWarnings
                messages={messages}
                testId={`AdvancedSettings__warnings-${axis}`}
            />
        ) : undefined;
    };

    return (
        <>
            <SectionDescription>
                These settings allow you to collect vibration data for X, Y, and Z axes or
                enveloped data for the Y-axis. Battery consumption increases as more
                settings are enabled.
            </SectionDescription>
            <SettingsList>
                <ToggleSetting
                    name="Vibration Data (X Axis)"
                    ariaLabel="Vibration Data (X Axis)"
                    helperText={axisWarnings('vibrationX')}
                    checked={vibrationX}
                    onChange={(checked) => setAxis('vibrationX', checked)}
                />
                <ToggleSetting
                    name="Vibration Data (Y Axis)"
                    ariaLabel="Vibration Data (Y Axis)"
                    helperText={axisWarnings('vibrationY')}
                    checked={vibrationY}
                    onChange={(checked) => setAxis('vibrationY', checked)}
                />
                {zAxisEnabled && (
                    <ToggleSetting
                        name="Vibration Data (Z Axis)"
                        ariaLabel="Vibration Data (Z Axis)"
                        helperText={axisWarnings('vibrationZ')}
                        checked={vibrationZ}
                        onChange={(checked) => setAxis('vibrationZ', checked)}
                    />
                )}

                {envelopedDataEnabled && <EnvelopedDataSetting />}

                <ToggleSetting
                    name="Ultrasound"
                    ariaLabel="Ultrasound"
                    helperText="High-frequency acoustic data will be recorded for early fault detection."
                    checked={ultrasound}
                    onChange={(checked) =>
                        setValue('ultrasound', checked, {
                            shouldDirty: true,
                            shouldValidate: true,
                        })
                    }
                />
                <ToggleSetting
                    name="Turning Speed"
                    ariaLabel="Turning Speed"
                    helperText="Estimates motor turning speed automatically. For driven equipment, speed is taken from the motor in the same assembly."
                    checked={turningSpeed}
                    onChange={(checked) =>
                        setValue('turningSpeed', checked, {
                            shouldDirty: true,
                            shouldValidate: true,
                        })
                    }
                />
            </SettingsList>
        </>
    );
}
